import { useEffect, useId, useState, type FormEvent } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Field } from '@/components/ui/Field/Field';
import { Icon } from '@/components/ui/Icon/Icon';
import { nb } from '@/i18n/nb';
import type { Poi, PoiCategory } from '@/types/api';
import { POI_CATEGORIES } from './poiCategories';
import s from './PoiFormModal.module.css';

type CommonProps = {
  open: boolean;
  onClose: () => void;
  isSaving: boolean;
};

type AddProps = CommonProps & {
  mode: 'add';
  // The lat/lng captured from the long-press / right-click event. Display-only —
  // the user can't edit them in the form (per the wireframe), and they're submitted
  // verbatim. PATCH never accepts coordinates, so edit-mode hides this readout.
  coordinates: { latitude: number; longitude: number };
  onSubmit: (input: {
    category: PoiCategory;
    name: string;
    comment: string | null;
    latitude: number;
    longitude: number;
  }) => void;
};

type EditProps = CommonProps & {
  mode: 'edit';
  initial: Poi;
  onSubmit: (input: {
    category: PoiCategory;
    name: string;
    comment: string | null;
  }) => void;
};

type Props = AddProps | EditProps;

/*
 * One form, two modes (add vs edit) — the wireframe puts them next to each other
 * and the only delta is the coordinates readout, so a single component avoids the
 * duplication. The seven categories render as a RadioGroup; the user must pick one
 * (no silent default into `other`, per ADR-0011).
 */
export function PoiFormModal(props: Props) {
  const initialCategory = props.mode === 'edit' ? props.initial.category : null;
  const initialName = props.mode === 'edit' ? props.initial.name : '';
  const initialComment = props.mode === 'edit' ? (props.initial.comment ?? '') : '';

  // Local form state — reset whenever the modal opens (closed-modal state is stale).
  const [category, setCategory] = useState<PoiCategory | null>(initialCategory);
  const [name, setName] = useState(initialName);
  const [comment, setComment] = useState(initialComment);
  const [errors, setErrors] = useState<{ category?: string; name?: string; comment?: string }>({});
  const groupLabelId = useId();

  useEffect(() => {
    if (props.open) {
      setCategory(initialCategory);
      setName(initialName);
      setComment(initialComment);
      setErrors({});
    }
    // initial* values are derived from props passed at open-time and won't change
    // mid-modal; we intentionally reset on every `open` flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  function validate(): boolean {
    const next: typeof errors = {};
    if (!category) next.category = nb.map.validation.categoryRequired;
    const trimmed = name.trim();
    if (trimmed.length === 0) next.name = nb.map.validation.nameRequired;
    else if (trimmed.length > 120) next.name = nb.map.validation.nameMax;
    if (comment.length > 500) next.comment = nb.map.validation.commentMax;
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!validate() || !category) return;
    const trimmedComment = comment.trim();
    const commentValue = trimmedComment.length > 0 ? trimmedComment : null;

    if (props.mode === 'add') {
      props.onSubmit({
        category,
        name: name.trim(),
        comment: commentValue,
        latitude: props.coordinates.latitude,
        longitude: props.coordinates.longitude,
      });
    } else {
      props.onSubmit({ category, name: name.trim(), comment: commentValue });
    }
  }

  const title = props.mode === 'add' ? nb.map.add.title : nb.map.edit.title;
  const lede = props.mode === 'add' ? nb.map.add.lede : nb.map.edit.lede;
  const submitLabel = props.mode === 'add' ? nb.map.add.submit : nb.map.edit.submit;

  return (
    <Modal open={props.open} onClose={props.onClose} title={title}>
      <p className={s.lede}>{lede}</p>
      <form className={s.form} onSubmit={handleSubmit} noValidate>
        <fieldset className={s.radioGroup} aria-labelledby={groupLabelId} aria-invalid={Boolean(errors.category)}>
          <legend id={groupLabelId} className={s.label}>
            {nb.map.add.categoryLabel}
          </legend>
          <div className={s.radioGrid}>
            {POI_CATEGORIES.map((cat) => {
              const isSelected = category === cat.id;
              return (
                <label
                  key={cat.id}
                  className={`${s.radio} ${isSelected ? s.radioOn : ''}`}
                  style={{ ['--cat-color' as string]: `var(${cat.tokenVar})` }}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.id}
                    checked={isSelected}
                    onChange={() => setCategory(cat.id)}
                    className={s.radioInput}
                  />
                  <span className={s.radioDot} aria-hidden="true">
                    <Icon name={cat.icon} size={14} />
                  </span>
                  <span>{nb.map.categories[cat.labelKey]}</span>
                </label>
              );
            })}
          </div>
          {errors.category ? (
            <span role="alert" className={s.errorMsg}>
              {errors.category}
            </span>
          ) : null}
        </fieldset>

        <Field label={nb.map.add.nameLabel} error={errors.name}>
          {(wiring) => (
            <input
              {...wiring}
              type="text"
              className={s.input}
              maxLength={120}
              placeholder={nb.map.add.namePlaceholder}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>

        <Field label={nb.map.add.commentLabel} error={errors.comment}>
          {(wiring) => (
            <textarea
              {...wiring}
              rows={3}
              className={`${s.input} ${s.textarea}`}
              maxLength={500}
              placeholder={nb.map.add.commentPlaceholder}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          )}
        </Field>

        {props.mode === 'add' ? (
          <div className={s.coords}>
            <Icon name="map" size={14} />
            <span className={s.coordsLabel}>{nb.map.add.coordsLabel}</span>
            {/* Display with 5-decimal precision (~1 m at this latitude) so the readout
                is meaningful without dominating the form; the server gets the full value. */}
            <span className={s.coordsValue}>
              {props.coordinates.latitude.toFixed(5)}, {props.coordinates.longitude.toFixed(5)}
            </span>
          </div>
        ) : null}

        <div className={s.actions}>
          <Button variant="secondary" onClick={props.onClose} type="button">
            {nb.map.add.cancel}
          </Button>
          <Button variant="primary" type="submit" loading={props.isSaving}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
